import { type ReactNode, useEffect, useState } from "react";

import { useUnit } from "effector-react";
import { IntlProvider } from "react-intl";

import { $currentLocale, setCurrentLocale } from "./model";
import { type Messages } from "./types";

type MessageGetter<L> = (locale: L) => Promise<Messages>;

export type I18nProviderProps<L> = {
  messagesGetter: MessageGetter<L>;
  renderChildren: (locale: L) => ReactNode;
};

type FetchMessageArg<L> = {
  locale: L;
  messagesGetter: MessageGetter<L>;
  onSuccess: (data: Messages) => void;
};

const fetchMessages = async <L extends string>({
  locale,
  messagesGetter,
  onSuccess,
}: FetchMessageArg<L>) => {
  const res = await messagesGetter(locale);
  onSuccess(res);
};

export const I18nProvider = <Locale extends string>({
  messagesGetter,
  renderChildren,
}: I18nProviderProps<Locale>) => {
  const [currentLocale] = useUnit([$currentLocale, setCurrentLocale]);

  const [messages, setMessages] = useState<Messages>();

  useEffect(() => {
    fetchMessages({
      locale: currentLocale as Locale,
      messagesGetter,
      onSuccess: setMessages,
    });
  }, [currentLocale, messagesGetter]);

  if (!currentLocale) {
    return null;
  }

  return (
    <IntlProvider
      key={currentLocale}
      locale={currentLocale}
      messages={messages}
    >
      {renderChildren(currentLocale as Locale)}
    </IntlProvider>
  );
};
